import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trophy, Sparkles, Loader2, Ticket } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

/**
 * LotteryWheelDialog — cinematic winner-picker.
 *
 * Props:
 *   open              boolean       — controls the modal
 *   onClose           () => void
 *   pool              [{ user_id, username, name, total_entries, picture }]
 *   winners           [{ tier, user_id, username, name, prize_vbucks, entries }]
 *                     Populated one at a time by the parent as each tier lands.
 *   targetCount       int           — total winners we're going to reveal
 *   phase             'spinning' | 'reveal' | 'done'
 *   spinKey           any           — bump this to (re)trigger a spin
 *   targetUserId      string        — the winner id the wheel should land on
 */
export default function LotteryWheelDialog({
  open, onClose, pool = [], winners = [], targetCount = 3,
  phase = 'spinning', spinKey = 0, targetUserId = null,
}) {
  const [angle, setAngle] = useState(0);
  const rotationRef = useRef(0); // accumulated rotation so consecutive spins stay smooth
  const wheelRef = useRef(null);
  const [wheelPx, setWheelPx] = useState(300);
  const MAX_WEDGES = 100;

  // Expand each user into one wedge per lottery entry (capped at MAX_WEDGES
  // total slices using largest-remainder allocation) so the visual odds match
  // the real odds. If a user has 5 tickets they appear on 5 wedges.
  const wedges = useMemo(() => {
    if (!pool.length) return [];
    // Sort by entries desc so the biggest holders are laid out first
    const sorted = [...pool].sort(
      (a, b) => (b.total_entries || 0) - (a.total_entries || 0)
    );
    const totalEntries = sorted.reduce((n, p) => n + (p.total_entries || 0), 0);
    if (totalEntries === 0) return sorted;

    let alloc;
    if (totalEntries <= MAX_WEDGES) {
      // 1 wedge per ticket, no scaling needed
      alloc = sorted.map((p) => ({ p, slots: p.total_entries || 0 }));
    } else {
      // Largest-remainder proportional allocation
      const shares = sorted.map((p) => {
        const raw = ((p.total_entries || 0) / totalEntries) * MAX_WEDGES;
        return { p, raw, floor: Math.floor(raw), remainder: raw - Math.floor(raw) };
      });
      alloc = shares.map((s) => ({ p: s.p, slots: Math.max(1, s.floor), remainder: s.remainder }));
      let totalAlloc = alloc.reduce((n, s) => n + s.slots, 0);
      // Trim if we over-allocated (from the "min 1" floor bump)
      while (totalAlloc > MAX_WEDGES) {
        let removeIdx = -1;
        let minRem = Infinity;
        for (let i = 0; i < alloc.length; i++) {
          if (alloc[i].slots > 1 && alloc[i].remainder < minRem) {
            minRem = alloc[i].remainder;
            removeIdx = i;
          }
        }
        if (removeIdx === -1) break;
        alloc[removeIdx].slots -= 1;
        totalAlloc -= 1;
      }
      // Distribute leftover slots to the users with the highest fractional parts
      const idxByRemDesc = alloc
        .map((s, i) => ({ i, rem: s.remainder }))
        .sort((a, b) => b.rem - a.rem);
      for (const { i } of idxByRemDesc) {
        if (totalAlloc >= MAX_WEDGES) break;
        alloc[i].slots += 1;
        totalAlloc += 1;
      }
    }

    // Interleave (round-robin) so the same @username doesn't sit in one giant
    // adjacent block — makes the wheel feel more varied while preserving odds.
    const out = [];
    const queues = alloc.filter((s) => s.slots > 0).map((s) => ({ p: s.p, remaining: s.slots }));
    while (queues.some((q) => q.remaining > 0)) {
      for (const q of queues) {
        if (q.remaining > 0) { out.push(q.p); q.remaining -= 1; }
      }
    }
    return out;
  }, [pool]);

  const N = wedges.length;
  // Scale label size + arc distance based on how many slices are on the wheel.
  const labelSize = N <= 12 ? 12 : N <= 24 ? 11 : N <= 48 ? 9 : N <= 80 ? 8 : 7;
  const labelTruncate = N <= 12 ? 14 : N <= 24 ? 12 : N <= 48 ? 10 : 8;
  // Place labels at 72% of the wheel radius (i.e., 36% of full diameter from center)
  const labelOffsetPx = Math.round(wheelPx * 0.36);

  useEffect(() => {
    if (!wheelRef.current) return undefined;
    const el = wheelRef.current;
    const readSize = () => setWheelPx(el.getBoundingClientRect().width || 300);
    readSize();
    const ro = new ResizeObserver(readSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  useEffect(() => {
    if (!targetUserId || N === 0) return;
    // The winner may occupy multiple wedges (one per entry). Pick a random
    // matching slice so consecutive spins for the same user don't always
    // land on the exact same wedge — it feels more like a live drawing.
    const matches = [];
    wedges.forEach((p, i) => { if (p.user_id === targetUserId) matches.push(i); });
    if (matches.length === 0) return;
    const idx = matches[Math.floor(Math.random() * matches.length)];
    // Angle-per-slice, and target so the pointer (top, 12 o'clock) points at idx.
    // We rotate the wheel by an angle such that slice `idx` sits at 0deg.
    const per = 360 / N;
    // Add ±40% of a slice as jitter so it looks organic (never dead-center).
    const jitter = (Math.random() - 0.5) * per * 0.8;
    // At least 5 full extra rotations plus one more per subsequent tier so
    // each reveal feels like a fresh, faster spin.
    const spinsBase = 5 + winners.length; // winners.length grows before this fires
    const rawTarget = -idx * per + jitter;
    // Snap final rotation so it's the smallest angle ≥ prev + spinsBase*360 whose
    // remainder mod 360 equals rawTarget (mod 360).
    const currentMod = ((rotationRef.current % 360) + 360) % 360;
    const targetMod = ((rawTarget % 360) + 360) % 360;
    let delta = targetMod - currentMod;
    if (delta > 0) delta -= 360; // always go the negative (visual "spin" clockwise-ish)
    const next = rotationRef.current + delta - spinsBase * 360;
    rotationRef.current = next;
    setAngle(next);
  }, [spinKey]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl bg-[#1a0440] border-[3px] border-yellow-300 text-white overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="lottery-wheel-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-yellow-300 font-black text-xl sm:text-2xl flex items-center gap-2">
            <Trophy className="h-5 w-5" strokeWidth={3} />
            V-Bucks Draw · {phase === 'done' ? 'Complete' : `Winner ${Math.min(winners.length + 1, targetCount)} of ${targetCount}`}
          </DialogTitle>
          <DialogDescription className="text-white/70 font-bold text-xs">
            {phase === 'done'
              ? 'That\'s a wrap — congratulations to our winners!'
              : phase === 'reveal'
                ? 'Locking in the winner…'
                : 'The wheel is spinning… hold onto your Sprites.'}
          </DialogDescription>
        </DialogHeader>

        {/* Wheel */}
        <div className="relative mx-auto w-[300px] h-[300px] sm:w-[380px] sm:h-[380px] shrink-0 select-none">
          {/* pointer (top) */}
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <div className="w-0 h-0 mx-auto"
              style={{
                borderLeft: '14px solid transparent',
                borderRight: '14px solid transparent',
                borderTop: '22px solid #ff2bd6',
                filter: 'drop-shadow(0 3px 0 #3a1064)',
              }}
            />
          </div>

          {/* wheel outer ring */}
          <div
            ref={wheelRef}
            className="absolute inset-0 rounded-full border-[6px] border-yellow-300 shadow-[0_0_0_4px_#3a1064,0_10px_30px_rgba(0,0,0,0.5)]"
            style={{
              background:
                'conic-gradient(from -90deg,' + wedges.map((_, i) => {
                  const colors = ['#ff2bd6','#00b8ff','#ffd900','#9d3dff','#0eb85e','#ff6f2b'];
                  const c = colors[i % colors.length];
                  const start = (i * 360) / (N || 1);
                  const end = ((i + 1) * 360) / (N || 1);
                  return `${c} ${start}deg ${end}deg`;
                }).join(',') + ')',
              transform: `rotate(${angle}deg)`,
              transition: 'transform 4.5s cubic-bezier(0.17, 0.67, 0.24, 1)',
            }}
          >
            {/* wedge labels */}
            {wedges.map((p, i) => {
              const per = 360 / N;
              const rot = i * per + per / 2; // center of each wedge
              const label = p.username || p.name || (p.email && p.email.split('@')[0]) || '?';
              return (
                <div
                  key={p.user_id}
                  className="absolute left-1/2 top-1/2 pointer-events-none"
                  style={{
                    // Rotate around the wheel's center, push outward by a real
                    // pixel distance derived from the measured wheel size, then
                    // rotate the text 90° so it reads tangent to the wheel.
                    transform: `rotate(${rot}deg) translateY(-${labelOffsetPx}px) rotate(90deg)`,
                    transformOrigin: '0 0',
                  }}
                >
                  <div
                    className="font-black uppercase text-white tracking-tight whitespace-nowrap"
                    style={{
                      transform: 'translate(-50%, -50%)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.75), 0 0 4px rgba(0,0,0,0.5)',
                      fontSize: `${labelSize}px`,
                    }}
                  >
                    @{label.slice(0, labelTruncate)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* hub */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-[#1a0440] border-[4px] border-yellow-300 flex items-center justify-center z-10 shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
            <Ticket className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-300" strokeWidth={3} />
          </div>

          {/* confetti sparks when a reveal is happening */}
          {phase === 'reveal' && (
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 20 }).map((_, i) => (
                <span
                  key={i}
                  className="absolute h-2 w-2 rounded-full spark"
                  style={{
                    left: `${50 + (Math.cos((i / 20) * Math.PI * 2) * 50)}%`,
                    top: `${50 + (Math.sin((i / 20) * Math.PI * 2) * 50)}%`,
                    background: ['#ffd900','#ff2bd6','#00b8ff','#9d3dff'][i % 4],
                    animationDelay: `${i * 40}ms`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Winners revealed so far */}
        <div className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
          {winners.length === 0 && (
            <div className="text-center text-yellow-300 font-black text-sm opacity-80 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Spinning up the first winner…
            </div>
          )}
          {winners.map((w) => (
            <div
              key={`w-${w.tier}`}
              data-testid={`wheel-winner-tier-${w.tier}`}
              className="rounded-2xl bg-white text-[#3a1064] border-[3px] border-yellow-300 p-3 flex items-center gap-3 shadow-[0_5px_0_#7a4a00] winner-pop"
            >
              <div className="h-10 w-10 rounded-full bg-[#ffd900] border-2 border-[#3a1064] text-[#3a1064] font-black text-lg flex items-center justify-center shrink-0">
                {w.tier}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-black truncate text-base">
                  {w.name || w.username || w.email || w.user_id}
                </div>
                <div className="text-[11px] font-bold text-[#3a1064]/70 truncate">
                  {w.username && `@${w.username}`} · {w.entries} entries
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] font-black uppercase text-[#3a1064]/60">V-Bucks</div>
                <div className="font-black text-[#ff2bd6] text-lg">{w.prize_vbucks?.toLocaleString?.() ?? w.prize_vbucks}</div>
              </div>
            </div>
          ))}
        </div>

        {phase === 'done' && (
          <button
            data-testid="lottery-wheel-close-btn"
            onClick={onClose}
            className="mt-3 h-10 rounded-xl bg-[#ffd900] text-[#3a1064] font-black text-sm border-2 border-yellow-300 hover:bg-yellow-300 flex items-center justify-center gap-2"
          >
            <Sparkles className="h-4 w-4" /> Close
          </button>
        )}

        <style>{`
          @keyframes svWinnerPop {
            0% { transform: scale(0.6); opacity: 0; }
            60% { transform: scale(1.08); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          .winner-pop { animation: svWinnerPop 500ms cubic-bezier(0.34, 1.56, 0.64, 1); }
          @keyframes svSpark {
            0% { transform: scale(0) translate(0, 0); opacity: 1; }
            100% { transform: scale(1.2) translate(var(--dx, 0), var(--dy, 0)); opacity: 0; }
          }
          .spark { animation: svSpark 900ms ease-out forwards; }
          @media (prefers-reduced-motion: reduce) {
            .winner-pop, .spark { animation: none; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
